// CF-ANALYTICS-FOUNDATION-V1 Ph-2: the IMPURE app-wide beacon shell.
//
// `events.ts` holds the PURE, unit-tested primitives (the immutable accumulator + flush decisions +
// beacon builder). This module is the thin DOM/network shell that composes them — the analogue of
// the demo-telemetry shell that lives inside CoinFlip.tsx, but lifted to one app-wide singleton so
// the App-shell lifecycle events (app_opened / session_started / session_ended) and the CoinFlip
// demo events (demo_flip_settled / demo_tier_selected / mode_switched) share ONE coherent
// per-app-open session and ONE batch. (Two parallel dispatchers would fragment the session and
// race the same flush.)
//
// Identity-FREE by construction: it posts { events: [...] } via apiFetch, which attaches the signed
// Telegram initData header; the backend derives (auth_bot, tg_user_id) and maps it to an opaque
// subject_id server-side. This module NEVER reads or sends tg_user_id / username / wallet / raw
// initData. Fire-and-forget: every dispatch swallows its rejection (.catch) so telemetry can never
// throw into game settlement. Variant-agnostic — no brand copy.

import { apiFetch } from './api';
import {
  type AnalyticsEventInput,
  type EventBatchState,
  appendEvent,
  buildEventsBeacon,
  emptyEventBatchState,
  newSessionId,
  shouldFlushByThreshold,
  shouldFlushOnHide,
} from './events';

// One session id per app-open (module load === one Mini App session). Shared by every recordEvent
// caller so a player's events in one open are groupable WITHOUT identity.
export const SESSION_ID: string = newSessionId();

// Module-scoped mutable batch (the impure cell). The pure ops return new state; we just hold the
// latest. `apiBase` is captured on first use so the App can pass it once.
let batch: EventBatchState = emptyEventBatchState();
let apiBaseCaptured: string | null = null;
let listenersInstalled = false;

/** Wire the app-wide visibility/pagehide flush ONCE (idempotent). Call from the App shell with the
 *  resolved apiBase. Safe to call repeatedly — only the first call installs listeners. */
export function installEventsBeacon(apiBase: string): void {
  apiBaseCaptured = apiBase;
  if (listenersInstalled) return;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  listenersInstalled = true;
  const onHide = () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
    if (shouldFlushOnHide(batch)) flushEvents();
  };
  const onPageHide = () => {
    if (shouldFlushOnHide(batch)) flushEvents();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
}

/**
 * Record one analytics event: stamp it (event_id/ts_ms/client_seq) into the shared batch, then
 * threshold-flush. The event input is identity-free (the caller must never pass identity). Never
 * throws — a flush failure is swallowed inside flushEvents.
 */
export function recordEvent(input: AnalyticsEventInput): void {
  batch = appendEvent(batch, { ...input, session_id: input.session_id ?? SESSION_ID });
  if (shouldFlushByThreshold(batch)) flushEvents();
}

/**
 * Flush the pending batch to POST /api/v1/events. Snapshot-then-reset BEFORE the network call so an
 * event recorded during the in-flight request accumulates fresh (no double-count, no loss). Fire-
 * and-forget: the rejection is swallowed (the events were already reset — we drop rather than retry,
 * preferring lost telemetry over double-counting). No-op when there is nothing to send or no apiBase
 * has been captured yet.
 */
export function flushEvents(): void {
  const beacon = buildEventsBeacon(batch);
  if (!beacon) return;
  if (apiBaseCaptured === null) return; // not wired yet — keep accumulating until installEventsBeacon
  batch = emptyEventBatchState();
  void apiFetch(`${apiBaseCaptured}/api/v1/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(beacon),
  }).catch((err) => {
    // Best-effort: drop the events (already reset) rather than retry. Never surface to play.
    console.warn('analytics events beacon failed', err);
  });
}

/** Test-only: reset the module batch + captured apiBase between cases. (Listeners are not removed —
 *  the unit tests exercise events.ts pure primitives, not this shell.) */
export function __resetEventsBeaconForTests(): void {
  batch = emptyEventBatchState();
  apiBaseCaptured = null;
}
